import { Link, NavLink, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { useAuth } from "@/lib/auth";
import { Wordmark } from "@/components/Wordmark";
import { BRAND } from "@/config/brand";
import { Button } from "@/components/ui/button";
import { isPreviewEnv } from "@/lib/env";
import api from "@/lib/api";
import { LogOut } from "lucide-react";

export function AppShell({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  // Access-driven Upgrade nav item: shown to signed-in users who do
  // NOT yet hold the Complete Assessment grant. Fetched once on mount
  // and refreshed only when the user changes. Silent fallback on
  // error (item just isn't shown) — never breaks the shell.
  const [access, setAccess] = useState(null);
  useEffect(() => {
    if (!user) { setAccess(null); return; }
    let cancelled = false;
    api.get("/me/access")
      .then((r) => { if (!cancelled) setAccess(r.data); })
      .catch(() => { if (!cancelled) setAccess(null); });
    return () => { cancelled = true; };
  }, [user]);
  const showUpgradeNav = !!user && access && !access.capabilities?.complete_assessment_access;

  const onLogout = async () => {
    await logout();
    navigate("/", { replace: true });
  };

  const NavItem = ({ to, label, testid }) => (
    <NavLink
      to={to}
      data-testid={testid}
      end={to === "/dashboard"}
      className={({ isActive }) =>
        `text-sm px-3 py-2 rounded-md transition-colors duration-200 ` +
        (isActive
          ? "text-fg-primary bg-bg-surface-hover"
          : "text-fg-secondary hover:text-fg-primary hover:bg-bg-surface-hover")
      }
    >
      {label}
    </NavLink>
  );

  // Signed-out visitors see a lightweight public header (Wordmark +
  // Pricing + Log in + Create account). This is only reached today
  // via /plans in signed-out mode (Mark's 2026-02 public-pricing
  // correction). No user menu, no logout, no authenticated nav.
  if (!user) {
    return (
      <div className="min-h-screen bg-bg-base text-fg-primary flex flex-col">
        <a href="#main-content" className="cra3d-skip-link" data-testid="skip-to-main">
          Skip to main content
        </a>
        <header
          data-testid="public-app-header"
          className="border-b border-border-subtle bg-bg-surface"
        >
          <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 min-h-16 py-2 flex flex-wrap items-center justify-between gap-y-2 gap-x-2">
            <Link to="/" data-testid="public-header-brand-link" className="min-w-0">
              <Wordmark size="sm" testid="public-header-wordmark" />
            </Link>
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm">
              <NavLink
                to="/plans"
                data-testid="public-nav-pricing"
                className={({ isActive }) =>
                  `px-3 py-2 rounded-md transition-colors duration-200 ` +
                  (isActive
                    ? "text-fg-primary bg-bg-surface-hover"
                    : "text-fg-secondary hover:text-fg-primary hover:bg-bg-surface-hover")
                }
              >
                Plans &amp; Pricing
              </NavLink>
              <Link
                to="/login"
                data-testid="public-nav-login"
                className="px-3 py-2 rounded-md text-fg-secondary hover:text-fg-primary hover:bg-bg-surface-hover transition-colors duration-200"
              >
                Log in
              </Link>
              <Link
                to="/register"
                data-testid="public-nav-register"
                className="px-3 py-2 rounded-md bg-accent-warm text-bg-base hover:bg-accent-warm-hover transition-colors duration-200"
              >
                Create account
              </Link>
            </div>
          </div>
        </header>
        <main id="main-content" tabIndex={-1} className="flex-1">{children}</main>
        <footer className="border-t border-border-subtle bg-bg-surface">
          <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 text-xs text-fg-secondary">
            <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2">
              <span data-testid="footer-copyright">© {new Date().getFullYear()} {BRAND.footer_identity}</span>
              {isPreviewEnv() && (
                <>
                  <span aria-hidden="true" className="hidden sm:inline text-fg-muted">·</span>
                  <span data-testid="footer-preview-notice" className="text-fg-muted">{BRAND.preview_notice}</span>
                </>
              )}
            </div>
            <div className="flex flex-wrap gap-4">
              <Link to="/privacy" data-testid="footer-privacy-link" className="hover:text-fg-primary transition-colors duration-200">Privacy</Link>
              <Link to="/terms" data-testid="footer-terms-link" className="hover:text-fg-primary transition-colors duration-200">Terms</Link>
              <Link to="/refund-policy" data-testid="footer-refund-policy-link" className="hover:text-fg-primary transition-colors duration-200">Refund Policy</Link>
            </div>
          </div>
        </footer>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-bg-base text-fg-primary flex flex-col">
      <a href="#main-content" className="cra3d-skip-link" data-testid="skip-to-main">
        Skip to main content
      </a>
      <header
        data-testid="app-header"
        className="border-b border-border-subtle bg-bg-surface"
      >
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/dashboard" data-testid="header-brand-link">
            <Wordmark size="sm" testid="header-wordmark" />
          </Link>
          <nav className="hidden md:flex items-center gap-1">
            <NavItem to="/dashboard" label="Dashboard" testid="nav-dashboard" />
            <NavItem to="/assessment" label="Assessment" testid="nav-assessment" />
            <NavItem to="/results" label="Results" testid="nav-results" />
            <NavItem to="/learning" label="Learning" testid="nav-learning" />
            <NavItem to="/training" label="AI Training" testid="nav-training" />
            <NavItem to="/portfolio" label="Portfolio" testid="nav-portfolio" />
            <NavItem to="/action-plan" label="Plan" testid="nav-plan" />
            <NavItem to="/plans" label="Plans & Pricing" testid="nav-plans-pricing" />
            <NavItem to="/settings" label="Settings" testid="nav-settings" />
            {showUpgradeNav && (
              <Link
                to="/plans?intent_slug=three_directions"
                data-testid="nav-upgrade"
                className="whitespace-nowrap text-sm px-3 py-2 rounded-md bg-accent-warm text-bg-base hover:bg-accent-warm-hover transition-colors duration-200"
              >
                Upgrade &mdash; $24.99
              </Link>
            )}
            {user?.role === "admin" && (
              <NavItem to="/admin" label="Admin" testid="nav-admin" />
            )}
          </nav>
          <div className="flex items-center gap-3">
            <span
              data-testid="user-email-badge"
              className="hidden sm:inline text-xs text-fg-secondary"
            >
              {user?.email}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={onLogout}
              data-testid="header-logout-btn"
              className="border-border-subtle text-fg-primary hover:bg-bg-surface-hover hover:text-fg-primary"
            >
              <LogOut className="h-4 w-4 mr-2" aria-hidden="true" />
              Log out
            </Button>
          </div>
        </div>
        {/* mobile nav — scrolls internally on narrow viewports so it
            never pushes the document beyond the screen width. */}
        <div className="md:hidden border-t border-border-subtle bg-bg-surface overflow-x-auto">
          <div className="mx-auto max-w-6xl px-2 py-2 flex items-center gap-1 whitespace-nowrap w-max">
            <NavItem to="/dashboard" label="Dashboard" testid="mnav-dashboard" />
            <NavItem to="/assessment" label="Assessment" testid="mnav-assessment" />
            <NavItem to="/results" label="Results" testid="mnav-results" />
            <NavItem to="/learning" label="Learning" testid="mnav-learning" />
            <NavItem to="/training" label="AI Training" testid="mnav-training" />
            <NavItem to="/portfolio" label="Portfolio" testid="mnav-portfolio" />
            <NavItem to="/action-plan" label="Plan" testid="mnav-plan" />
            <NavItem to="/plans" label="Plans & Pricing" testid="mnav-plans-pricing" />
            <NavItem to="/settings" label="Settings" testid="mnav-settings" />
            {showUpgradeNav && (
              <Link
                to="/plans?intent_slug=three_directions"
                data-testid="mnav-upgrade"
                className="whitespace-nowrap text-sm px-3 py-2 rounded-md bg-accent-warm text-bg-base hover:bg-accent-warm-hover transition-colors duration-200"
              >
                Upgrade &mdash; $24.99
              </Link>
            )}
            {user?.role === "admin" && (
              <NavItem to="/admin" label="Admin" testid="mnav-admin" />
            )}
          </div>
        </div>
      </header>
      <main id="main-content" tabIndex={-1} className="flex-1">{children}</main>
      <footer className="border-t border-border-subtle bg-bg-surface">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 text-xs text-fg-secondary">
          <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2">
            <span data-testid="footer-copyright">© {new Date().getFullYear()} {BRAND.footer_identity}</span>
            {isPreviewEnv() && (
              <>
                <span aria-hidden="true" className="hidden sm:inline text-fg-muted">·</span>
                <span data-testid="footer-preview-notice" className="text-fg-muted">{BRAND.preview_notice}</span>
              </>
            )}
          </div>
          <div className="flex gap-4">
            <Link to="/privacy" data-testid="footer-privacy-link" className="hover:text-fg-primary transition-colors duration-200">Privacy</Link>
            <Link to="/terms" data-testid="footer-terms-link" className="hover:text-fg-primary transition-colors duration-200">Terms</Link>
            <Link to="/refund-policy" data-testid="footer-refund-policy-link" className="hover:text-fg-primary transition-colors duration-200">Refund Policy</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default AppShell;
